const XYCHART_HEADER = /^\s*xychart(-beta)?\b/m;
const AXIS_LINE = /^(\s*)([xy]-axis)\s*(.*)$/;
// `[title] min [-->|->|-|–|to|..] max` — the grammar only accepts `-->` between the bounds.
const AXIS_RANGE = /^(.*?)\s*(-?[\d.]+)\s*(?:-->|->|--|-|–|—|to|\.\.|,)?\s*(-?[\d.]+)\s*$/i;
const AXIS_CATEGORIES = /^(.*?)\s*(\[.*\])\s*$/;
const TITLE_LINE = /^(\s*)title\s+(.*)$/;
const SERIES_LINE = /^(\s*)(bar|line)\s*(.*?)\s*\[([^\]]*)\]\s*$/;

/** @param {string} text */
function quote(text) {
  const inner = text.trim().replace(/^"(.*)"$/, '$1').replace(/"/g, "'");
  return inner ? `"${inner}"` : '';
}

/** @param {string} raw */
function parseNumber(raw) {
  const value = Number(raw.replace(/[$%,\s]/g, ''));
  return raw.trim() && Number.isFinite(value) ? value : undefined;
}

// `[$1,200, 45%]` — prefer splitting on a comma followed by whitespace so
// thousands separators inside a value survive; fall back to a bare comma.
/** @param {string} raw */
function parseValues(raw) {
  const parts = /,\s/.test(raw) ? raw.split(/,\s+/) : raw.split(',');
  const values = parts.map(parseNumber);
  return values.every((value) => value !== undefined) ? values : undefined;
}

/** @param {...string} parts */
function join(...parts) {
  return parts.filter(Boolean).join(' ');
}

/** @param {string} line */
function normalizeAxis(line) {
  const [, indent, keyword, text] = /** @type {RegExpMatchArray} */ (line.match(AXIS_LINE));

  const categories = text.match(AXIS_CATEGORIES);
  if (categories) {
    return `${indent}${join(keyword, quote(categories[1]), categories[2])}`;
  }

  const range = text.match(AXIS_RANGE);
  if (range) {
    const [, title, min, max] = range;
    if (parseNumber(min) !== undefined && parseNumber(max) !== undefined) {
      return `${indent}${join(keyword, quote(title), `${min} --> ${max}`)}`;
    }
  }

  return `${indent}${join(keyword, quote(text))}`;
}

/**
 * Repairs the xychart-beta syntax slips LLMs commonly make (a missing `-->` in an
 * axis range, unquoted chart/axis/series titles, `$`/`%`/thousands separators in
 * data values) so the chart renders instead of failing to parse.
 *
 * @param {string} chart
 * @returns {string}
 */
export function normalizeXyChart(chart) {
  if (!XYCHART_HEADER.test(chart)) return chart;

  return chart
    .split('\n')
    .map((line) => {
      if (AXIS_LINE.test(line)) return normalizeAxis(line);

      const title = line.match(TITLE_LINE);
      if (title) return `${title[1]}title ${quote(title[2])}`;

      const series = line.match(SERIES_LINE);
      if (series) {
        const [, indent, keyword, title, rawValues] = series;
        const values = parseValues(rawValues);
        if (values) return `${indent}${join(keyword, quote(title), `[${values.join(', ')}]`)}`;
      }

      return line;
    })
    .join('\n');
}
